import React, { useEffect, useState } from 'react';
import { apiFetch, getApiHost } from '../utils/api';

// Format date as relative time (e.g., "1m ago", "2h ago", "3d ago")
const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    return `${diffMonths}mo ago`;
};

interface SocialMention {
    accountHandle: string;
    accountQuality: number;
    tweetText: string;
    tweetUrl: string;
    viewCount: number;
    timestamp: Date;
}

interface EntityVerification {
    representsEntity: boolean;
    entityType?: 'project' | 'kol' | 'brand' | 'organization' | 'celebrity' | 'other';
    entityName?: string;
    officiallyVerified: boolean;
    verificationEvidence?: string;
    verificationUrl?: string;
}

interface SocialResearchResult {
    mint: string;
    tokenDescriptionAnalysis?: string;
    socialActivityAnalysis?: string;
    noveltyAnalysis?: string;
    imageDescription?: string;
    entityVerification?: EntityVerification;
    totalMentions: number;
    mentions: SocialMention[];
    totalViews: number;
    noveltyRank?: number;
    relevanceRank?: number;
    overallSocialSentiment?: 'positive' | 'negative' | 'neutral';
    overallTrendingPotentialRank?: number;
    researchedAt: Date;
}

interface SocialResearchSummaryProps {
    mint: string;
    /**
     * Optional callback rendered as a "Refresh research (1 credit)"
     * button when the social research is missing or stale (>1h old).
     * Wired by V2; V1 doesn't pass this and keeps its prior behaviour
     * (return null when missing, no per-section refresh).
     */
    onRequestResearch?: () => void;
    requestingResearch?: boolean;
}

const STALE_RESEARCH_MS = 60 * 60 * 1000; // 1 hour

const SocialResearchSummary: React.FC<SocialResearchSummaryProps> = ({ mint, onRequestResearch, requestingResearch }) => {
    const [data, setData] = useState<SocialResearchResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [expandedMention, setExpandedMention] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await apiFetch(`${getApiHost()}/api/social_research/${mint}`);
                if (!response.ok) {
                    if (response.status === 404) {
                        setError('no-data');
                        return;
                    }
                    throw new Error('Failed to fetch social research data');
                }
                const data = await response.json();
                setData(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [mint]);

    // Determine sentiment colors
    const getSentimentStyles = (sentiment?: string) => {
        if (!sentiment) return 'text-gray-400';
        switch (sentiment.toLowerCase()) {
            case 'positive': return 'text-green-400';
            case 'negative': return 'text-red-400';
            default: return 'text-gray-400';
        }
    };

    // Determine value level colors
    const getValueStyles = (value: number) => {
        if (value >= 7) return 'text-green-400';
        if (value >= 4) return 'text-yellow-400';
        return 'text-gray-400';
    };

    // Format large numbers
    const formatNumber = (num: number) => {
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num.toString();
    };

    // Loading state
    if (loading) {
        return (
            <div className="bg-crypto-card rounded-xl mb-6 opacity-70">
                <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-700">
                    <h3 className="text-xl font-bold flex items-center">
                        <i className="fas fa-share-alt mr-2 text-blue-400"></i>
                        Social Research
                    </h3>
                </div>
                <div className="text-gray-400 text-center py-4">Loading social data...</div>
            </div>
        );
    }

    // No data state.
    // - With onRequestResearch (V2): render a small CTA so the user
    //   can spend 1 credit to kick off social research right here
    //   instead of needing to find the global Refresh button.
    // - Without onRequestResearch (V1): preserve old behaviour and
    //   render nothing (ShillTweets may still show X mentions).
    if (error || !data) {
        if (!onRequestResearch) return null;
        return (
            <div className="bg-crypto-card rounded-xl mb-6">
                <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-700">
                    <h3 className="text-xl font-bold flex items-center">
                        <i className="fas fa-share-alt mr-2 text-blue-400"></i>
                        Social Research
                    </h3>
                </div>
                <div className="flex flex-wrap items-center gap-3 py-2">
                    <span className="text-sm text-gray-400">No social research has been run for this token yet.</span>
                    <button
                        onClick={onRequestResearch}
                        disabled={requestingResearch}
                        className="px-3 py-1 text-xs font-medium rounded-lg border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 transition disabled:opacity-50"
                    >
                        {requestingResearch ? 'Requesting…' : 'Run research (1 credit)'}
                    </button>
                </div>
            </div>
        );
    }

    const isStale = onRequestResearch
        ? (Date.now() - new Date(data.researchedAt).getTime()) > STALE_RESEARCH_MS
        : false;

    return (
        <div className="bg-crypto-card rounded-xl mb-6">
            <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-700">
                <h3 className="text-xl font-bold flex items-center">
                    <i className="fas fa-share-alt mr-2 text-blue-400"></i>
                    Social Research
                </h3>
                <span className="text-xs text-gray-500 flex items-center gap-2">
                    {formatRelativeTime(data.researchedAt)}
                    {isStale && onRequestResearch && (
                        <button
                            onClick={onRequestResearch}
                            disabled={requestingResearch}
                            className="px-2 py-0.5 text-[11px] font-medium rounded border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 transition disabled:opacity-50"
                        >
                            {requestingResearch ? 'Requesting…' : 'Refresh research (1 credit)'}
                        </button>
                    )}
                </span>
            </div>

            <div className="flex flex-col gap-4">
                {/* Key Metrics Row */}
                <div className="flex flex-wrap gap-4">
                    {data.overallSocialSentiment && (
                        <div className="flex flex-col items-center min-w-[80px] px-3 py-2 bg-crypto-gray/50 rounded-lg border border-gray-700">
                            <span className={`text-base font-semibold whitespace-nowrap ${getSentimentStyles(data.overallSocialSentiment)}`}>
                                {data.overallSocialSentiment === 'positive' ? '😊' : 
                                 data.overallSocialSentiment === 'negative' ? '😟' : '😐'}
                                {' '}{data.overallSocialSentiment.charAt(0).toUpperCase() + data.overallSocialSentiment.slice(1)}
                            </span>
                            <span className="text-xs text-gray-500 uppercase tracking-wider mt-1">Sentiment</span>
                        </div>
                    )}

                    {data.overallTrendingPotentialRank !== undefined && (
                        <div className="flex flex-col items-center min-w-[80px] px-3 py-2 bg-crypto-gray/50 rounded-lg border border-gray-700">
                            <span className={`text-base font-semibold whitespace-nowrap ${getValueStyles(data.overallTrendingPotentialRank)}`}>
                                🔥 {data.overallTrendingPotentialRank}/10
                            </span>
                            <span className="text-xs text-gray-500 uppercase tracking-wider mt-1">Potential</span>
                        </div>
                    )}

                    <div className="flex flex-col items-center min-w-[80px] px-3 py-2 bg-crypto-gray/50 rounded-lg border border-gray-700">
                        <span className="text-base font-semibold whitespace-nowrap text-white">
                            👁️ {formatNumber(data.totalViews)}
                        </span>
                        <span className="text-xs text-gray-500 uppercase tracking-wider mt-1">Views</span>
                    </div>

                    <div className="flex flex-col items-center min-w-[80px] px-3 py-2 bg-crypto-gray/50 rounded-lg border border-gray-700">
                        <span className="text-base font-semibold whitespace-nowrap text-white">
                            💬 {data.totalMentions}
                        </span>
                        <span className="text-xs text-gray-500 uppercase tracking-wider mt-1">Mentions</span>
                    </div>

                    {data.noveltyRank !== undefined && (
                        <div className="flex flex-col items-center min-w-[80px] px-3 py-2 bg-crypto-gray/50 rounded-lg border border-gray-700">
                            <span className={`text-base font-semibold whitespace-nowrap ${getValueStyles(data.noveltyRank)}`}>
                                ✨ {data.noveltyRank}/10
                            </span>
                            <span className="text-xs text-gray-500 uppercase tracking-wider mt-1">Novelty</span>
                        </div>
                    )}

                    {data.relevanceRank !== undefined && (
                        <div className="flex flex-col items-center min-w-[80px] px-3 py-2 bg-crypto-gray/50 rounded-lg border border-gray-700">
                            <span className={`text-base font-semibold whitespace-nowrap ${getValueStyles(data.relevanceRank)}`}>
                                🎯 {data.relevanceRank}/10
                            </span>
                            <span className="text-xs text-gray-500 uppercase tracking-wider mt-1">Relevance</span>
                        </div>
                    )}
                </div>

                {/* Entity Verification */}
                {data.entityVerification?.representsEntity && (
                    <div className={`px-3 py-3 rounded-lg border-l-4 ${
                        data.entityVerification.officiallyVerified
                            ? 'bg-green-500/10 border-green-500'
                            : 'bg-orange-500/10 border-orange-500'
                    }`}>
                        <div className="flex items-center gap-2 mb-2">
                            {data.entityVerification.officiallyVerified ? (
                                <span className="text-green-400 text-sm font-semibold flex items-center gap-1.5">
                                    <i className="fas fa-check-circle"></i>
                                    Verified {data.entityVerification.entityType === 'kol' ? 'KOL' : 
                                              data.entityVerification.entityType ? data.entityVerification.entityType.charAt(0).toUpperCase() + data.entityVerification.entityType.slice(1) : 'Entity'} Token
                                </span>
                            ) : (
                                <span className="text-orange-400 text-sm font-semibold flex items-center gap-1.5">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    Unverified {data.entityVerification.entityType === 'kol' ? 'KOL' : 
                                                data.entityVerification.entityType ? data.entityVerification.entityType.charAt(0).toUpperCase() + data.entityVerification.entityType.slice(1) : 'Entity'} Token
                                </span>
                            )}
                            {data.entityVerification.entityName && (
                                <span className="text-xs text-gray-400 bg-crypto-dark px-2 py-0.5 rounded-full">
                                    Claims: {data.entityVerification.entityName}
                                </span>
                            )}
                        </div>
                        {data.entityVerification.verificationEvidence && (
                            <p className="text-sm text-gray-300 leading-relaxed">
                                {data.entityVerification.verificationEvidence}
                            </p>
                        )}
                        {data.entityVerification.verificationUrl && (
                            <a
                                href={data.entityVerification.verificationUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mt-2"
                            >
                                <i className="fas fa-external-link-alt text-[10px]"></i>
                                Official verification link
                            </a>
                        )}
                    </div>
                )}

                {/* Token Description */}
                {data.tokenDescriptionAnalysis && (
                    <div className="px-4 py-4 bg-crypto-gray/30 rounded-lg border-l-4 border-crypto-accent">
                        <div className="flex items-center gap-2 mb-2">
                            <i className="fas fa-file-alt text-crypto-accent text-sm"></i>
                            <h4 className="text-sm font-semibold text-white">Token Description</h4>
                        </div>
                        <p className="text-sm text-gray-400 leading-relaxed pl-6">
                            {data.tokenDescriptionAnalysis}
                        </p>
                    </div>
                )}

                {/* Social Activity Analysis */}
                {data.socialActivityAnalysis && (
                    <div className="px-4 py-4 bg-crypto-gray/30 rounded-lg border-l-4 border-purple-500">
                        <div className="flex items-center gap-2 mb-2">
                            <i className="fas fa-comments text-purple-400 text-sm"></i>
                            <h4 className="text-sm font-semibold text-white">Social Activity</h4>
                        </div>
                        <p className="text-sm text-gray-400 leading-relaxed pl-6">
                            {data.socialActivityAnalysis}
                        </p>
                    </div>
                )}

                {/* Novelty Analysis */}
                {data.noveltyAnalysis && (
                    <div className="px-4 py-4 bg-crypto-gray/30 rounded-lg border-l-4 border-yellow-500">
                        <div className="flex items-center gap-2 mb-2">
                            <i className="fas fa-lightbulb text-yellow-400 text-sm"></i>
                            <h4 className="text-sm font-semibold text-white">Novelty Analysis</h4>
                        </div>
                        <p className="text-sm text-gray-400 leading-relaxed pl-6">
                            {data.noveltyAnalysis}
                        </p>
                    </div>
                )}

                {/* Top Mention Preview */}
                {data.mentions && data.mentions.length > 0 && (
                    <div className="px-3 py-3 bg-crypto-gray/50 rounded-lg border border-gray-700">
                        <div className="flex flex-wrap gap-3 items-center mb-2">
                            <a
                                href={`https://x.com/${data.mentions[0].accountHandle}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:underline font-medium text-sm"
                            >
                                @{data.mentions[0].accountHandle}
                            </a>
                            <span className="text-xs text-gray-500 bg-crypto-dark px-2 py-0.5 rounded-full">
                                Quality: {data.mentions[0].accountQuality}/10
                            </span>
                            <span className="text-xs text-gray-500 bg-crypto-dark px-2 py-0.5 rounded-full">
                                {formatNumber(data.mentions[0].viewCount)} views
                            </span>
                            {data.mentions[0].timestamp && (
                                <span className="text-xs text-gray-500 bg-crypto-dark px-2 py-0.5 rounded-full">
                                    {formatRelativeTime(data.mentions[0].timestamp)}
                                </span>
                            )}
                        </div>
                        <a
                            href={data.mentions[0].tweetUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-sm text-gray-400 italic leading-relaxed hover:text-gray-300"
                        >
                            {expandedMention || data.mentions[0].tweetText.length <= 150
                                ? data.mentions[0].tweetText
                                : data.mentions[0].tweetText.substring(0, 150) + '...'
                            }
                        </a>
                        {data.mentions[0].tweetText.length > 150 && (
                            <button
                                onClick={() => setExpandedMention(!expandedMention)}
                                className="text-xs text-blue-400 hover:text-blue-300 mt-2 cursor-pointer"
                            >
                                {expandedMention ? 'Show less' : 'Show more'}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SocialResearchSummary;
